'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useBadgeCounts, useNotifications } from '@/features/notifications/hooks/use-notifications';

/**
 * Watcher global pentru notificari noi: cand contul de unread creste,
 * declanseaza un ping audio (Web Audio API, fara fisier) si o notificare
 * sistem (Notification API, daca utilizatorul a permis).
 *
 * Mountat o singura data in (dashboard)/layout.tsx ca sa pacaleasca pe toate
 * paginile fara duplicare. Returneaza nimic vizual.
 *
 * Comportament:
 * - Prima oara cand este montat, NU declanseaza nimic (initializeaza baseline).
 * - La fiecare crestere a unread count, ping + notificare desktop.
 * - Cere permisiunea Notification API o singura data, fara nag.
 * - Daca tab-ul are focus pe pagina /admin/support* sau /support*, sare ping-ul
 *   (utilizatorul deja se uita la conversatie).
 */
export function useNotificationAlerts() {
  const router = useRouter();
  const { data: badgeData } = useBadgeCounts();
  const { data: notificationsData } = useNotifications({ limit: '5' });

  const previousCount = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const permissionRequested = useRef(false);

  // Cere permisiunea sistem o singura data (la primul user gesture sau dupa idle)
  useEffect(() => {
    if (permissionRequested.current) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'default') {
      permissionRequested.current = true;
      return;
    }

    // Asteapta o interactiune cu pagina inainte de a cere — Chrome blocheaza
    // requesturile la load, plus e mai politicos.
    const askOnce = () => {
      if (permissionRequested.current) return;
      permissionRequested.current = true;
      Notification.requestPermission().catch(() => undefined);
      window.removeEventListener('click', askOnce);
      window.removeEventListener('keydown', askOnce);
    };
    window.addEventListener('click', askOnce, { once: true });
    window.addEventListener('keydown', askOnce, { once: true });
    return () => {
      window.removeEventListener('click', askOnce);
      window.removeEventListener('keydown', askOnce);
    };
  }, []);

  // Detecteaza cresterea contului si declanseaza ping + notificare
  useEffect(() => {
    // Asteapta incarcarea datelor inainte de a seta baseline. Altfel,
    // primul fetch declanseaza notificare false: 0 (default cat timp
    // loading) -> N (cand fetch raspunde) e interpretat ca "crestere".
    if (badgeData?.unreadTotal === undefined) return;

    const current = badgeData.unreadTotal;

    if (previousCount.current === null) {
      previousCount.current = current;
      return;
    }

    if (current > previousCount.current) {
      const newOnes = current - previousCount.current;
      const onSupportPage = typeof window !== 'undefined' &&
        (window.location.pathname.startsWith('/admin/support') ||
         window.location.pathname.startsWith('/support'));

      if (!onSupportPage) {
        playPing(audioCtxRef);
        showSystemNotification(notificationsData?.data?.[0], newOnes, router);
      }
    }

    previousCount.current = current;
  }, [badgeData?.unreadTotal, notificationsData?.data, router]);
}

/**
 * Ping audio sintetizat — un ton scurt (E5 → A5) cu fade-out, ~200ms.
 * Fara dependenta de fisier audio.
 */
function playPing(ctxRef: React.MutableRefObject<AudioContext | null>) {
  try {
    if (typeof window === 'undefined') return;
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;

    if (!ctxRef.current) ctxRef.current = new AudioCtx();
    const ctx = ctxRef.current;

    // Resume in caz ca a fost suspended (autoplay policy)
    if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);

    const now = ctx.currentTime;
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(659.25, now); // E5
    osc1.frequency.exponentialRampToValueAtTime(880, now + 0.08); // A5

    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(987.77, now + 0.06); // B5 (overlap)

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);

    osc1.start(now);
    osc2.start(now + 0.06);
    osc1.stop(now + 0.25);
    osc2.stop(now + 0.25);
  } catch {
    // Audio nu functioneaza — silent fail
  }
}

interface NotificationLike {
  id: string;
  title?: string;
  body?: string;
  data?: { ticketId?: string; href?: string } | Record<string, unknown> | null;
}

function showSystemNotification(
  latest: NotificationLike | undefined,
  newOnes: number,
  router: ReturnType<typeof useRouter>,
) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    const title = latest?.title ?? (newOnes > 1 ? `${newOnes} notificari noi` : 'Notificare noua');
    const body = latest?.body ?? '';
    // Deep-link strict din `data.href` (fiecare tip de notificare îl setează —
    // ex. support: /admin/support/:id). NU reconstrui din ticketId, ca sa nu
    // presupui ruta /admin pt un user care nu e admin (cross-role). Fara href →
    // lista generala de notificari, sigura pt orice rol.
    const href =
      (latest?.data as { href?: string } | undefined)?.href ?? '/notificari';

    const notif = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: latest?.id ?? `nf-${Date.now()}`,
    });

    notif.onclick = () => {
      window.focus();
      router.push(href);
      notif.close();
    };

    // Auto-close dupa 8 secunde (browserele moderne pot face asta singure)
    setTimeout(() => notif.close(), 15000);
  } catch {
    // Notificarea esuata — nu e critic
  }
}
